import { Spacing } from '@persona-id/design-tokens';
import { debounce } from 'lodash/fp';
import { ChangeEvent, useCallback, useEffect, useMemo, useState, VFC } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import MaximizableCodeEditor from 'dashboard/components/MaximizableCodeEditor';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import ReactSelect, { OptionType } from 'dashboard/components/ReactSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import { StepAdvancedConfigurationWithErrorHandlingPanel } from 'dashboard/components/WorkflowControlPanel/StepAdvancedConfigurationWithErrorHandlingPanel';
import { ParamsBaseProps } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ParamsBaseProps';
import RetryIntervals from 'dashboard/components/WorkflowControlPanel/StepConfigAction/RetryIntervals';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import CodeHighlight from 'dashboard/components-lib/CodeHighlight';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import {
  Trigger,
  TRIGGER_OBJECT_TYPE_TO_WORKFLOW_FIELD_OBJECT_KEY,
} from 'dashboard/models/trigger';
import { Workflow, WorkflowError } from 'dashboard/models/workflow';
import { WorkflowActionEvaluateCodeV1 } from 'dashboard/models/workflow-version';
import { RootState } from 'dashboard/redux/store';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { Label, Paragraph } from 'design-system/components/Text';
import TextArea from 'design-system/components/TextArea';
import { toBase64WithUnicode, toStringFromBase64WithUnicode } from 'lib/base64';
import { EventType } from 'lib/constants/events';
import useApiCallback from 'lib/hooks/useApiCallback';
import useIsPersonerd from 'lib/hooks/useIsPersonerd';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';
import { assertNever } from 'lib/utils/types';
import { eventTypeFor, eventTypeForComputedFieldTrigger } from 'lib/workflows';

const LabelAndButton = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const InlineInput = styled('div')`
  margin-top: ${Spacing[2]};
  width: ${({ theme }) => theme.element.width.md};
`;

interface ParamsEvaluateCodeV1Props extends ParamsBaseProps {
  editable: boolean;
  errors: WorkflowError[];
  params: WorkflowActionEvaluateCodeV1['params'];
  trigger?: Trigger;
  updateActionParams: (newParams: WorkflowActionEvaluateCodeV1['params']) => void;
}

const RETURN_TYPES: Record<WorkflowActionEvaluateCodeV1['params']['return-type'], string> = {
  void: 'Nothing',
  boolean: 'Boolean',
  number: 'Number',
  string: 'String',
};

const returnTypeOptions: OptionType<WorkflowActionEvaluateCodeV1['params']['return-type']>[] =
  Object.entries(RETURN_TYPES).map(([value, label]) => ({
    value: value as WorkflowActionEvaluateCodeV1['params']['return-type'],
    label,
  }));

const SAMPLE_CODE = `/**
 * Evaluate Code
 *
 * Persona's API key on current environment is available as env.PERSONA_API_KEY
 */

/**
 * evaluateCodeStep will be invoked when this step runs
 *
 * @param {object} triggerObject - For event workflows, this is the object that triggers the workflow.
 * The object structure matches what the API returns. https://docs.withpersona.com/reference/introduction
 * @param {variable} triggerParams - For API workflows, this is the object that triggers the workflow.
 * The object structure matches the JSON schema defined in the trigger configuration.
 *
 * @returns {(void|null|boolean|number|string)} The value returned from function should
 * be of the same type that you set in the custom code step, and can be referenced in subsequent steps.
 * Note that either returning null or not returning both satisfy the 'void' return type.
 */
async function evaluateCodeStep(triggerObject, triggerParams) {
  // YOUR CODE HERE
}
`;

const ParamsEvaluateCodeV1: VFC<ParamsEvaluateCodeV1Props> = ({
  editable,
  errors,
  params,
  trigger,
  updateActionParams,
  step,
  updateStep,
}) => {
  const deserializedCode = params['serialized-code']
    ? toStringFromBase64WithUnicode(params['serialized-code'])
    : SAMPLE_CODE;

  const updateCode = (newCode: string) => {
    updateActionParams({
      ...params,
      ['serialized-code']: toBase64WithUnicode(newCode),
    });
  };

  const updateRetryIntervals = (
    retryIntervals: WorkflowActionEvaluateCodeV1['params']['retry-intervals'],
  ) => {
    updateActionParams({
      ...params,
      ['retry-intervals']: retryIntervals,
    });
  };

  const updateReturnType = (newType: WorkflowActionEvaluateCodeV1['params']['return-type']) => {
    updateActionParams({
      ...params,
      ['return-type']: newType,
    });
  };

  const [apiTriggerParams, setApiTriggerParams] = useState({});
  const [
    isValidApiTriggerParams,
    { enable: markAsValidApiTriggerParams, disable: markAsInvalidApiTriggerParams },
  ] = useToggle(true);
  const updateParameters = (event: ChangeEvent<HTMLTextAreaElement>) => {
    debouncedUpdateParameters(event.target.value);
  };
  const debouncedUpdateParameters = useCallback(
    debounce(500, (newParam: string) => {
      try {
        setApiTriggerParams(JSON.parse(newParam));
        markAsValidApiTriggerParams();
      } catch (_e) {
        markAsInvalidApiTriggerParams();
      }
    }),
    [setApiTriggerParams, markAsValidApiTriggerParams, markAsInvalidApiTriggerParams],
  );

  const [isShownTestModal, { enable: showTestModal, disable: hideTestMModal }] = useToggle(false);

  // TODO: Get rid of these logic; isPersonerd, isBugcrowdTester at the end
  // This is a quick workaround to discourage customers from editing the Evaluate Code action step during 'beta' phase
  // Backend doesn't have this restriction, so anyone who has write access for workflow on allowed org can edit
  // We only present the edit UI to Personerds; Impersonating a user who has "Workflow Update" permission, or a user has both '@{withpersona,bugcrowdninja}.com' email and the permission
  const isPersonerd = useIsPersonerd();
  const isBugcrowdTester = useSelector<RootState, boolean>((state) =>
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    state.currentUser.currentUser.attributes['email-address'].endsWith('@bugcrowdninja.com'),
  );

  const isCodeEditable = useMemo(
    () => editable && (isPersonerd || isBugcrowdTester),
    [editable, isPersonerd, isBugcrowdTester],
  );

  const environmentId = useSelector<RootState, string>(
    (state) => state.currentEnvironment.currentEnvironment.id,
  );

  const { renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionEvaluateCodeV1['params']>(errors);

  const renderAboveEditor = () => (
    <>
      <Label size='md' htmlFor='params-evaluate-code__return-type-input' weight='bold'>
        Expected type of returning value
      </Label>
      <InlineInput>
        <ReactSelect<WorkflowActionEvaluateCodeV1['params']['return-type']>
          id='params-evaluate-code__return-type'
          value={params['return-type']}
          options={returnTypeOptions}
          onChange={updateReturnType}
          disabled={!isCodeEditable}
        />
      </InlineInput>
      <Spacer height={2} />
      <LabelAndButton>
        <Label size='md' weight='bold'>
          Code
        </Label>
        {isCodeEditable && params['serialized-code'] && isValidApiTriggerParams && (
          <Button
            id='params-evaluate-code__code-run'
            aria-label='Run this code'
            width='auto'
            variant='basic'
            onClick={showTestModal}
          >
            Run this code
          </Button>
        )}
        {!isValidApiTriggerParams && <>API Trigger params are invalid</>}
      </LabelAndButton>
      <Spacer height={2} />
      {isShownTestModal && trigger && (
        <ModalWrapper isOpen={isShownTestModal} handleCloseModal={hideTestMModal}>
          <RunCodeModal
            params={params}
            trigger={trigger}
            environmentId={environmentId}
            onClose={hideTestMModal}
            triggerParams={apiTriggerParams}
          />
        </ModalWrapper>
      )}
    </>
  );

  const renderBelowEditor = () => {
    if (
      trigger?.type === ApiModel.TriggerApi ||
      trigger?.type === ApiModel.TriggerCase ||
      trigger?.type === ApiModel.TriggerAccount
    ) {
      return (
        <>
          <Spacer height={2} />
          <Label size='md' htmlFor='params-evaluate-code__trigger-params' weight='bold'>
            Trigger params
          </Label>
          <TextArea
            id='params-evaluate-code__trigger-params'
            onChange={updateParameters}
            defaultValue='{}'
          />
        </>
      );
    }
  };

  return (
    <>
      <Spacer height={2} />
      <ParamLabel labelText='Code' />
      <Paragraph>
        This code is expected to return &ldquo;{RETURN_TYPES[params['return-type']]}&rdquo;. This
        step will fail if a different type of value is returned.
      </Paragraph>
      <Spacer height={1} />
      <MaximizableCodeEditor
        code={deserializedCode}
        disableDebounce
        language='javascript'
        modalTitle='Evaluate Code'
        isOutsideClickDisabled={isShownTestModal}
        isReadOnly={!isCodeEditable}
        onCodeUpdate={updateCode}
        renderAboveEditor={renderAboveEditor}
        renderBelowEditor={renderBelowEditor}
      />
      {renderErrorMessageFor('serialized-code')}
      <Spacer height={4} />
      <StepAdvancedConfigurationWithErrorHandlingPanel
        step={step}
        trigger={trigger}
        updateStep={updateStep}
        disabled={!editable}
      >
        <RetryIntervals
          editable={editable}
          retryIntervals={params['retry-intervals']}
          updateRetryIntervals={updateRetryIntervals}
        />
      </StepAdvancedConfigurationWithErrorHandlingPanel>
    </>
  );
};

export default ParamsEvaluateCodeV1;

const RunCodeModal: VFC<{
  params: WorkflowActionEvaluateCodeV1['params'];
  trigger: Trigger;
  environmentId: string;
  onClose: () => void;
  triggerParams: Record<string | number, unknown>;
}> = ({
  params: { 'return-type': returnType, 'serialized-code': serializedCode },
  trigger,
  environmentId,
  onClose,
  triggerParams,
}) => {
  const triggerObjectType = useMemo(() => {
    switch (trigger.type) {
      case ApiModel.TriggerCase:
        return 'case';
      case ApiModel.TriggerAccount:
        return 'account';
      case ApiModel.TriggerApi:
        return null;
      case ApiModel.TriggerDirect:
        return 'inquiry';
      case ApiModel.TriggerSchedule:
      case ApiModel.TriggerRecurring:
        return TRIGGER_OBJECT_TYPE_TO_WORKFLOW_FIELD_OBJECT_KEY[
          trigger.attributes['trigger-object-type']
        ];
      case ApiModel.TriggerEvent:
        switch (eventTypeFor(trigger)) {
          case EventType.Case:
            return 'case';
          case EventType.CaseMessage:
            return 'case message';
          case EventType.Inquiry:
            return 'inquiry';
          case EventType.Report:
            return 'report';
          case EventType.Transaction:
            return 'transaction';
          default:
            return 'inquiry';
        }
      case ApiModel.TriggerComputedField:
        switch (eventTypeForComputedFieldTrigger(trigger)) {
          case EventType.Case:
            return 'case';
          case EventType.CaseMessage:
            return 'case message';
          case EventType.Inquiry:
            return 'inquiry';
          case EventType.Report:
            return 'report';
          case EventType.Transaction:
            return 'transaction';
          default:
            return 'inquiry';
        }
      default:
        assertNever(trigger);
    }
  }, [trigger]);

  const [response, setResponse] = useState<{ result: boolean; payload: string; logs: unknown[][] }>(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    null,
  );

  const { callback: evaluateCode, loading } = useApiCallback(
    async (code: string) => {
      const { meta } = await SessionClientDashboard.postResourceSingletonAction<
        Workflow,
        { type: ApiModel.Workflow },
        { result: boolean; payload: string; logs: string }
      >({ type: ApiModel.Workflow }, 'try-evaluate-code', {
        meta: {
          'environment-id': environmentId,
          'return-type': returnType,
          'serialized-code': code,
          'trigger-object-type': triggerObjectType,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'trigger-params':
            triggerObjectType == null ||
            trigger.type === ApiModel.TriggerCase ||
            trigger.type === ApiModel.TriggerAccount
              ? triggerParams
              : undefined,
        },
      });
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setResponse({
        ...meta,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        payload: meta.payload != null ? JSON.parse(meta.payload) : meta.payload,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        logs: meta.logs != null ? JSON.parse(meta.logs) : meta.logs,
      });
    },
    [setResponse],
  );

  useEffect(() => {
    if (serializedCode) {
      evaluateCode(serializedCode);
    }
  }, [evaluateCode, serializedCode]);

  return (
    <ModalContainer>
      <ModalHeader onClose={onClose}>Evaluate Code</ModalHeader>
      <ModalBody>
        {loading && <LoadingIndicator />}
        {response && (
          <>
            <Label size='lg' weight='bold'>
              Result
            </Label>
            <div>{response.result ? 'Succeeded' : 'Failed'}</div>
            <Spacer height={2} />
            {response.result ? (
              <>
                <Label size='lg' weight='bold'>
                  Return Value
                </Label>
                <CodeHighlight language='json'>{JSON.stringify(response.payload)}</CodeHighlight>
              </>
            ) : (
              <>
                <Label size='lg' weight='bold'>
                  Error Message
                </Label>
                <Paragraph>{response.payload}</Paragraph>
              </>
            )}
            <Spacer height={2} />
            <Label size='lg' weight='bold'>
              Debug Logs
            </Label>
            {response.logs.length > 0 ? (
              <>
                <CodeHighlight language='json' light>
                  {response.logs
                    .map((log) => log.map((val) => JSON.stringify(val)).join(' '))
                    .join('\n')}
                </CodeHighlight>
              </>
            ) : (
              <>
                <Paragraph>
                  No logs by <code>console.log</code>
                </Paragraph>
              </>
            )}
          </>
        )}
      </ModalBody>
    </ModalContainer>
  );
};
